import { API_BASE_URL } from '../config'

// ============================================================================
// Demo-Modus-Sperre
// ----------------------------------------------------------------------------
// Der öffentliche Demo-Modus (/demo) läuft rein clientseitig. Damit KEIN
// Feature versehentlich das Backend anspricht, wird hier window.fetch einmalig
// umschlossen: Alle Requests ans API-Backend werden abgefangen, zeigen einen
// Hinweis und schlagen kontrolliert fehl. Dadurch ist jedes zukünftige Feature,
// das das Backend anspricht, automatisch gesperrt — ohne jede Aufrufstelle
// einzeln anfassen zu müssen.
// ============================================================================

/** True, wenn die App über den öffentlichen Demo-Link (/demo) aufgerufen wurde. */
export function isDemoMode(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/+$/, '') === '/demo'
  )
}

/** Event, über das die Demo-Sperre eine Hinweis-Meldung an die UI schickt. */
export const DEMO_LOCKED_EVENT = 'demo-locked'

/** Standard-Hinweis, wenn ein gesperrtes Feature im Demo-Modus benutzt wird. */
export const DEMO_LOCKED_MESSAGE =
  'Dieses Feature ist in der Demo-Version gesperrt. Melde dich an, um es zu nutzen.'

/** Feuert einen globalen Hinweis, dass ein Feature im Demo-Modus gesperrt ist. */
export function notifyDemoLocked(message: string = DEMO_LOCKED_MESSAGE): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(DEMO_LOCKED_EVENT, { detail: { message } }))
}

/** Prüft, ob eine URL auf unser API-Backend zeigt. */
function isBackendRequest(url: string): boolean {
  if (!url) return false
  // Absolute Backend-URL ODER relativer API-Pfad (/api/...)
  if (API_BASE_URL && url.startsWith(API_BASE_URL)) return true
  return url.includes('/api/')
}

/** Extrahiert die Ziel-URL aus den unterschiedlichen fetch()-Aufrufformen. */
function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

let installed = false

/**
 * Sperrt im Demo-Modus ALLE Backend-Aufrufe. Wird nur im Demo-Modus und nur
 * einmal aktiv. Requests ans API-Backend werden abgefangen, zeigen einen
 * Hinweis und liefern eine 403-Antwort zurück, sodass aufrufende Services
 * kontrolliert fehlschlagen.
 */
export function installDemoApiLock(): void {
  if (installed || !isDemoMode()) return
  installed = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    if (isBackendRequest(resolveUrl(input))) {
      notifyDemoLocked()
      return new Response(
        JSON.stringify({ error: 'demo_locked', message: DEMO_LOCKED_MESSAGE }),
        {
          status: 403,
          statusText: 'Demo mode',
          headers: { 'Content-Type': 'application/json' },
        }
      )
    }

    return originalFetch(input, init)
  }
}
